import type { TFunction } from 'i18next';
import type { ReadinessIssue, ReadinessResult } from '@unipostudio/shared-validators';
import { formatDateTime, formatNumber, formatRelative } from '@/shared/lib/intl';
import type { Post } from '@/shared/services/posts.service';

/**
 * The pre-publish check in words (Task 1.2, docs/features/post-readiness.md).
 * Issues carry i18n keys: the check's own (`readiness.issue.*`) or, for a
 * broken channel rule, the composer's (`composer.rules.issue.*`,
 * `youtube.issue.*`, `composer.gbp.*`), so every screen says it the same way.
 */

/** Statuses a verdict is about: a post that already went out has none. */
const WAITING: ReadonlySet<string> = new Set(['SCHEDULED', 'PENDING_APPROVAL']);

/** The stored verdict of a post still waiting to publish, when it is not ready. */
export function postRisk(post: Pick<Post, 'status' | 'readiness'>): ReadinessResult | null {
  const r = post.readiness;
  if (!r || r.status === 'ready' || !WAITING.has(post.status)) return null;
  return r.issues.length > 0 ? r : null;
}

/** One issue's sentence in the reader's language. */
export function readinessIssueText(issue: Pick<ReadinessIssue, 'messageKey' | 'params'>, t: TFunction): string {
  const values: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(issue.params ?? {})) {
    if (k === 'typeKey') values['type'] = t(String(v));
    else if (k === 'at' && typeof v === 'string') values['at'] = formatDateTime(v);
    else values[k] = typeof v === 'number' ? formatNumber(v) : v;
  }
  return String(t(issue.messageKey, values));
}

/** The button that fixes an issue. */
export function readinessActionLabel(issue: Pick<ReadinessIssue, 'action' | 'params'>, t: TFunction): string {
  return String(t(`readiness.action.${issue.action}`, { platform: issue.params?.['platform'] ?? '' }));
}

/** "Goes out in 3 hours", for a post's time. */
export function goesOutText(scheduledAt: string | null | undefined, t: TFunction): string | null {
  return scheduledAt ? String(t('readiness.goesOut', { when: formatRelative(scheduledAt) })) : null;
}

/** The badge's words: its verdict and its first reason, for the tooltip and the screen reader. */
export function riskBadgeLabel(risk: ReadinessResult, t: TFunction): string {
  const first = risk.issues[0];
  const reason = first ? readinessIssueText(first, t) : '';
  return String(t(risk.status === 'blocked' ? 'readiness.badge.blocked' : 'readiness.badge.atRisk', { reason }));
}
